"use client"

import { AppContextValue, useAppContext } from "@/lib/context/app-context"
import { addItemToCart } from "@/lib/checkout/client"
import { umamiTrackEvent } from "@/lib/utils/umami"
import { Dispatch, SetStateAction, useRef, useState } from "react"
import { StoreProductVariant } from "@medusajs/types"
import { getPricesForVariant } from "@/lib/helpers/get-product-price"
import { Button } from "@/components/atoms"
import { DoneIcon } from "@/icons"
import useLocaleRouter from "@/hooks/useLocaleRouter"
import { useRouter } from "next/navigation"
import { ButtonProps } from "@/components/atoms/Button/Button"
import { useIframeEvent } from "@/context/IframeContext"
import { toast } from "@/lib/helpers/toast"
import { flyToCart } from "@/components/skins/barebones/flyToCart"


// add the selected variant to the cart
export const handleAddVariantToCart = async ({
    variant,
    title,
    locale,
    setLoading,
    appContext,
}: {
    variant: StoreProductVariant,
    title: string,
    locale: string,
    setLoading?: Dispatch<SetStateAction<boolean>>
    appContext: AppContextValue,
}) => {
    const variantId = variant?.id || variant?.sku
    if (!variantId) return false

    const variantPrice = getPricesForVariant(variant)
    if (!variantPrice) {
        toast.error({
            title: "No price for current region",
            description: "This variant is not priced for the selected country.",
        })
        return false
    }

    setLoading && setLoading(true)

    try {
        appContext.analytics?.("add_to_cart", {
            currency: variantPrice?.currency_code,
            value: variantPrice?.calculated_price,
            items: [{
                item_name: title,
                item_id: variant?.sku
            }]
        })
        umamiTrackEvent("add_to_cart", appContext, {
            currency: variantPrice?.currency_code,
            value: variantPrice?.calculated_price,
            items: [{
                item_name: title,
                item_id: variant?.sku
            }]
        })

        await addItemToCart({
            variantId: variantId,
            quantity: 1,
            countryCode: locale,
        })
        return true
    } catch (e: any) {
        toast.error({
            title: "Could not add to cart",
            description: e?.message || "Please try another size or region.",
        })
        return false
    } finally {
        setLoading && setLoading(false)
    }
}


type ButtonAddToCartProps = ButtonProps & {
    children: React.ReactNode
    variant: StoreProductVariant,
    title: string,
    locale: string,
    className?: string,
    setLoading?: Dispatch<SetStateAction<boolean>>
    redirectTo?: string,
    /** Optional product image for barebones fly-to-cart */
    thumbnail?: string | null,
};

// A button for adding variant cart
export const ButtonAddToCart = ({
    children,
    variant,
    title,
    locale,
    setLoading,
    redirectTo,
    className,
    thumbnail,
    ...props
}: ButtonAddToCartProps) => {
    const appContext = useAppContext()
    const localeRouter = useLocaleRouter(useRouter())
    const [effecting, setEffecting] = useState(false)
    const buttonRef = useRef<HTMLSpanElement>(null)
    let iframeEvent = useIframeEvent()
    const onClick = async (e: any) => {
        setEffecting(true);
        const ok = await handleAddVariantToCart({
            variant,
            title,
            locale,
            setLoading,
            appContext,
        });
        if (ok) {
            iframeEvent.sentRefreshDataToIframe?.()
            if (
                typeof document !== "undefined" &&
                document.querySelector(".theme-barebones")
            ) {
                flyToCart(buttonRef.current, thumbnail)
            }
            if (redirectTo) {
                localeRouter.push(redirectTo)
                setTimeout(() => setEffecting(false), 1000);
                return
            }
        }
        setEffecting(false);
    }

    return (
        <span ref={buttonRef} className="inline-flex w-full max-w-full">
            <Button
                className={className}
                onClick={onClick}
                {...props}
            >
                {effecting ? (<DoneIcon size={22} color="#26df26" />) : children}
            </Button>
        </span>
    )
}